import CachedIcon from "mdi-react/CachedIcon";
import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Button,
  InputGroup,
  Dropdown,
  Modal,
  Row,
  Col,
  Spinner,
  Badge,
} from "react-bootstrap";
import TableComponent from "../TableComponent";
import {
  DeleteIcon,
  FilterCollapseIcon,
  FilterTwoIcon,
  LoaderIcon,
  ViewIcon,
  AddCircleIcon,
  EditIcon,
} from "../Icons";
import { useLocalStorage, useQueryParams } from "../../utils/hooks";
import useDebounce from "../../utils/hooks";
import { useQuery, useMutation } from "react-query";
import { useAuth } from "../../hooks/useAuth";
import queryString from "query-string";
import {
  formatDate,
  paginationOptions,
  fetchActionsUtil,
} from "../../utils/helpers";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import ReactPaginate from "react-paginate";
import NoTableItem from "../utils/NoTableItem";
import { isEmpty, truncate } from "lodash";
import ConfirmDialog from "../ConfirmDialogue";
import { toast } from "react-toastify";
import SchemeModal from "./SchemeModal";

function Scheme() {
  const { backendUrl, token, user } = useAuth();
  const [showFilter, setShowFilter] = useLocalStorage(
    "showCoursesFilter",
    true,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState(null);

  const [showSchemeModal, setShowScheme] = useState(false);

  const initialFilterParams = {
    searchQuery: "",
  };

  const [queryParams, setQueryParams] = useQueryParams({
    page: 1,
    limit: 40,
    ...initialFilterParams,
  });

  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);

  const fetchScheme = async (query) => {
    try {
      const data = await fetchActionsUtil(
        `${backendUrl}/api/warehouse/get-scheme-data?${queryString.stringify(
          query,
        )}`,
        "GET",
        token,
      );
      return data;
    } catch (error) {
      toast.error("Error occurred, please refresh the page");
      throw error;
    }
  };

  const deleteScheme = useMutation(
    (id) =>
      fetchActionsUtil(
        `${backendUrl}/api/warehouse/delete-scheme`,
        "DELETE",
        token,
        { id },
      ),
    {
      onMutate: () => {
        setIsLoading(true);
      },
      onSuccess: ({ data }) => {
        refetch();
        toast.success(data?.message || "Deleted");
        setIsLoading(false);
      },
      onError: (error) => {
        toast.error(error?.message || "Unable to perform action");
        setIsLoading(false);
      },
    },
  );

  const deleteSchemeGuide = async (id) => {
    if (
      await ConfirmDialog({
        title: "Are you sure you want to delete this?",
        description: "You cannot retrieve a deleted scheme.",
        type: "danger",
      })
    ) {
      deleteScheme.mutate(id);
    }
  };

  // Sync debounced filter params with query params
  useEffect(() => {
    setQueryParams({ ...queryParams, ...debouncedFilterParams });
  }, [debouncedFilterParams]);

  const { data, refetch, isFetching, isSuccess } = useQuery(
    ["Scheme", queryParams],
    () => fetchScheme(queryParams),
    {
      enabled: true,
      keepPreviousData: true,
    },
  );

  const handleFilterParamsChange = (e) => {
    const { name, value } = e.target;
    setFilterParams({
      ...filterParams,
      [name]: value,
    });
  };

  const search = () => {
    setQueryParams({
      ...queryParams,
      ...filterParams,
    });
  };

  const reset = () => {
    setFilterParams(initialFilterParams);
    setQueryParams({
      ...queryParams,
      ...initialFilterParams,
    });
  };

  const handleSearchQueryChange = (e) => {
    setQueryParams({
      ...queryParams,
      [e.target.name]: e.target.value,
    });
  };

  const tableHead = () => {
    return (
      <thead className="position-sticky top-0">
        <tr>
          <th>Name</th>
          <th>Description</th>

          <th>Created At</th>
          <th>Actions</th>
        </tr>
      </thead>
    );
  };

  const tableData = (el, index) => {
    return (
      <>
        <td>{el?.title || "-"}</td>
        <td>{truncate(el?.description, { length: 40 })}</td>

        <td>{formatDate(el?.created_at)}</td>
        <td>
          <Dropdown>
            <Dropdown.Toggle
              variant=""
              className="bg-white border-0"
              bsPrefix="print more"
            >
              <DotsVerticalIcon size={20} />
            </Dropdown.Toggle>

            <Dropdown.Menu
              popperConfig={{
                strategy: "fixed",
              }}
              renderOnMount
              className="dropdown-with-icons"
            >
              <Dropdown.Item
                as={"button"}
                onClick={() => {
                  setSelectedScheme(el);
                  setShowScheme(true);
                }}
              >
                <EditIcon className="me-2" size={16} />
                Edit
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                as={"button"}
                className="text-danger"
                onClick={() => deleteSchemeGuide(el.id)}
              >
                <DeleteIcon className="me-2" size={16} />
                Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </td>
      </>
    );
  };

  return (
    <>
      <main className="cash-book">
        <div className="content">
          <div className="content-main pt-3">
            <div className={`filter-area ${showFilter ? "open" : "close"}`}>
              <div className="filter-header">
                <FilterTwoIcon /> Filters
                <Button
                  variant="white"
                  onClick={() => setShowFilter(!showFilter)}
                >
                  <FilterCollapseIcon />
                </Button>
              </div>
              <div className="filter-body">
                <Form>
                  <div className="body">
                    <Form.Group className="mb-3 pb-2 mb-2-5">
                      <Form.Label>Search </Form.Label>
                      <InputGroup>
                        <Form.Control
                          name="searchQuery"
                          value={filterParams.searchQuery}
                          placeholder="Search..."
                          onChange={handleFilterParamsChange}
                        />
                      </InputGroup>
                    </Form.Group>

                    {/* <Form.Group className="mb-3 pb-2 mb-2-5">
                      <Form.Label>Partner</Form.Label>
                      <Select
                        classNamePrefix="form-select"
                        placeholder="Certification Category"
                        name="courseCategory"
                        value={courseCategoryOption.find(
                          (cert) => cert.value === filterParams?.courseCategory,
                        )}
                        onChange={(e) => {
                          setFilterParams({
                            ...filterParams,
                            courseCategory: e?.value || "",
                          });
                        }}
                        options={courseCategoryOption}
                        isClearable
                      />
                    </Form.Group> */}

                    <hr className="mt-3 mb-4" />
                  </div>

                  <div className="buttons rounded">
                    <Button onClick={reset} variant="white">
                      Reset
                    </Button>
                    <Button onClick={search} variant="primary">
                      Search
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
            <div className="content-body">
              <header>
                <h1>
                  {!showFilter && (
                    <button
                      onClick={() => setShowFilter(!showFilter)}
                      className="btn filter"
                    >
                      <FilterTwoIcon />
                    </button>
                  )}
                  Scheme
                  <button
                    title="Refresh"
                    className="btn text-primary"
                    onClick={() => {
                      refetch();
                    }}
                  >
                    <CachedIcon />
                  </button>
                </h1>
                <div className="actions">
                  <Button
                    variant="primary"
                    onClick={() => {
                      setSelectedScheme(null);
                      setShowScheme(true);
                    }}
                  >
                    <AddCircleIcon className="me-2 " />+ Create New
                  </Button>
                </div>
              </header>

              <div className="m-3">
                <TableComponent
                  responsive
                  borderless
                  striped
                  tableHeadsFunction={tableHead}
                  mainDataArray={data?.data || []}
                  className="product-table text-nowrap"
                  tableDataRowFunction={tableData}
                />

                {!isFetching && isSuccess && isEmpty(data?.data) ? (
                  <NoTableItem queryParams={queryParams} />
                ) : null}
              </div>

              <div className="d-flex justify-content-between px-3 align-items-center pagination">
                <div className="pagination_left">
                  <p className="m-0 p-0">Show</p>
                  <select
                    value={queryParams.limit}
                    name="limit"
                    className="form-select"
                    onChange={(e) => handleSearchQueryChange(e)}
                  >
                    <option value="10">10 rows</option>
                    <option value="20">20 rows</option>
                    <option value="30">30 rows</option>
                    <option value="40">40 rows</option>
                    <option value="50">50 rows</option>
                    <option value="100">100 rows</option>
                  </select>
                </div>

                <ReactPaginate
                  {...paginationOptions}
                  pageCount={
                    Math.ceil(
                      (data?.count || data?.data?.length || 0) /
                        queryParams.limit,
                    ) || 1
                  }
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={0}
                  onPageChange={({ selected }) => {
                    document.body.scrollTop = document.documentElement.scrollTop = 0;
                    setQueryParams({
                      ...queryParams,
                      page: selected + 1,
                    });
                  }}
                  forcePage={queryParams.page - 1}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {showSchemeModal && (
        <SchemeModal
          show={showSchemeModal}
          key={new Date().getTime()}
          id={selectedScheme?.id}
          onSuccess={() => {
            refetch();
            setShowScheme(false);
          }}
          onHide={() => setShowScheme(false)}
          schemeData={selectedScheme}
        />
      )}

      {isLoading && (
        <div className="global-spinner">
          <LoaderIcon className="spin text-primary" />
        </div>
      )}
    </>
  );
}

export default Scheme;
